/* eslint-disable tss-unused-classes/unused-classes */

import camelCase from 'lodash/camelCase';
import React, { forwardRef } from 'react';
import { makeStyles } from 'tss-react/mui';

import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';

import QTip from 'components/QuickenControls/QTip';

const useStyles = makeStyles()(() => ({
  root: {
    lineHeight: '1em',
  },
  rootSmall: {
    fontSize: '20px',
    padding: '10px',
  },
  rootSmallTarget: {
    fontSize: '20px',
    padding: '6px',
    height: 32,
    width: 32,
  },
  rootSmallNoPadding: {
    fontSize: '20px',
    padding: '0px',
  },
  rootMedium: {
    fontSize: '24px',
    padding: '12px',
  },
  rootMediumNoPadding: {
    fontSize: '24px',
    padding: '3px',
  },
  rootLarge: {
    fontSize: '36px',
    padding: '12px',
  },
}));

const getSize = (theme: any) => (theme.defaults.dense ? 'small' : 'medium');

interface QIconButtonProps extends Omit<IconButtonProps, 'size'> {
  id?: string;
  children?: any;
  IconComponent?: React.ElementType<SvgIconProps> | React.ComponentType<any> | null;
  IconProps?: SvgIconProps;
  size?: 'small' | 'medium' | 'large' | 'smallNoPadding' | 'small-no-padding' | 'small-target' | 'medium-no-padding';
  tooltip?: any;
  tooltipProps?: any;
  TooltipProps?: any;
}

const QIconButton = forwardRef<HTMLButtonElement, QIconButtonProps>(
  (
    {
      children,
      IconComponent = null,
      IconProps,
      size,
      tooltip = null,
      tooltipProps,
      TooltipProps,
      ...other
    }: QIconButtonProps,
    ref,
  ) => {
    const theme = useTheme();
    const { classes, cx } = useStyles();

    size = typeof size === 'string' ? size : getSize(theme); // eslint-disable-line no-param-reassign

    let child: JSX.Element | null = null;
    if (IconComponent != null) {
      child = <IconComponent fontSize='inherit' {...IconProps} />;
    } else {
      child = children && React.cloneElement(children, { fontSize: 'inherit', ...children.props });
    }

    return (
      <IconButton
        classes={{
          // @ts-expect-error ts(7053)
          root: cx(classes.root, classes[camelCase(`root-${size}`)]),
        }}
        size='large'
        {...other}
        ref={ref}
      >
        {tooltip && (
          <QTip title={tooltip} {...TooltipProps}>
            {child}
          </QTip>
        )}
        {!tooltip && child}
      </IconButton>
    );
  },
);

QIconButton.displayName = 'QIconButton';

export default QIconButton;
